/**
 * Copyright (c) 2022, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import React, { FunctionComponent, ReactElement } from "react";
import { Button, ButtonGroup, Container, Content, Footer, Panel } from "rsuite";
import { AppHeader } from "../components";
import { useConfig } from "../hooks";
import { IdentifiableComponentInterface } from "../models/core";
import { canShowLogInButton, getSignInURL } from "../utils";

/**
 * Application signed out page props interface.
 */
export type SignedOutPagePropsInterface = IdentifiableComponentInterface;

/**
 * Application signed out page.
 *
 * @param {SignedOutPagePropsInterface} props - Props injected to the component.
 * @return {React.ReactElement}
 */
export const SignedOutPage: FunctionComponent<SignedOutPagePropsInterface> = (
    props: SignedOutPagePropsInterface
): ReactElement => {

    const {
        ["data-componentid"]: componentId
    } = props;

    const { deployment } = useConfig();

    return (
        <div data-componentid={ componentId }>
            <Container>
                <AppHeader
                    data-componentid="landing-page-app-header"
                    showGoToAsgardeoLink={ true }
                    showSignInButton={ canShowLogInButton() }
                    onSignIn={ () => window.open(getSignInURL(), "_self") }
                />
                <Content>
                    <div>
                        <div className="header-landing">
                            <div className="welcome-text-log-out-content">
                                <p>You&apos;ve successfully logged out of the Try It App </p>
                            </div>
                        </div>
                        <div className="logged-out-illustration" />
                        <p className="body-text-logout">
                            You&apos;ve experienced the login flows of the Asgardeo through Try It App.
                        </p>
                        <Panel
                            bordered 
                            className="logout-page-panel" 
                            header={
                                "Asgardeo has many more capabilities which help you manage " +
                                    "identity of your organization. Some of these features are as follows: "
                            }
                        >
                            <ButtonGroup vertical>
                                <Button 
                                    appearance="link"
                                    onClick={ () => {
                                        window.open(deployment?.documentation?.guides?.userAttributes,
                                            "_blank", "noopener,noreferrer");
                                    } }
                                    color="orange"
                                >
                                        User Management
                                </Button>
                                <Button 
                                    appearance="link"
                                    onClick={ () => {
                                        window.open(deployment?.documentation?.guides?.mfa,
                                            "_blank", "noopener,noreferrer");
                                    } }
                                    color="orange"
                                >
                                        Multi-Factor Authentication
                                </Button>
                                <Button 
                                    appearance="link"
                                    onClick={ () => {
                                        window.open(deployment?.documentation?.guides?.conditionalAuth,
                                            "_blank", "noopener,noreferrer");
                                    } }
                                    color="orange"
                                >
                                        Conditional Authentication
                                </Button>
                                <Button 
                                    appearance="link"
                                    onClick={ () => {
                                        window.open(deployment?.documentation?.guides?.configureSPA,
                                            "_blank", "noopener,noreferrer");
                                    } }
                                    color="orange"
                                >
                                        Configure your own applications
                                </Button>
                            </ButtonGroup>
                        </Panel>
                    </div>
                </Content>
                <Footer>
                    <div className="footer">
                        { deployment?.ui?.appCopyright }
                    </div>
                </Footer>
            </Container>
        </div>
    );
};
